import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, Copy, MapPin, DollarSign, Power, PowerOff, AlertTriangle, FileText, Star, Globe, Calendar, Clock, TrendingUp, ChevronDown, ChevronUp, MessageSquare, Users, Zap, Bot, Mail, CalendarDays } from 'lucide-react';
import { useFreelanceRestrictions } from '../hooks/useFreelanceStatus';
import { toast } from 'sonner';
import ResourceFormComplete from '../components/ResourceFormComplete';
import SearchAndViewToggle from '../components/SearchAndViewToggle';
import ResourceListView from '../components/ResourceListView';
import BonusTrialModal from '../components/BonusTrialModal';
import { isRMRole, isOperatingPartnerRole } from '../utils/roles';
import { 
  SERVICENOW_CERTIFICATIONS, 
  SERVICENOW_MODULES,
  RESOURCE_ROLES,
  SENIORITY_LEVELS,
  LANGUAGES,
  OCCUPATION_RATES,
  REMOTE_PERCENTAGES,
  COUNTRIES,
  CURRENCIES
} from '../constants/projectData';
import { getCertificationAcronym } from '../constants/certifications';

// Helper to get module acronym
const getModuleAcronym = (module) => {
  const acronymMap = {
    'IT Service Management (ITSM)': 'ITSM',
    'IT Operations Management (ITOM)': 'ITOM',
    'IT Business Management (ITBM)': 'ITBM',
    'Customer Service Management (CSM)': 'CSM',
    'Field Service Management (FSM)': 'FSM',
    'Human Resources Service Delivery (HRSD)': 'HRSD',
    'Security Operations (SecOps)': 'SecOps',
    'Governance, Risk and Compliance (GRC)': 'GRC',
    'IT Asset Management (ITAM)': 'ITAM',
    'Software Asset Management (SAM)': 'SAM',
    'Hardware Asset Management (HAM)': 'HAM',
    'Configuration Management Database (CMDB)': 'CMDB',
    'Service Catalog': 'Catalog',
    'Knowledge Management': 'Knowledge',
    'Change Management': 'Change',
    'Incident Management': 'Incident',
    'Problem Management': 'Problem',
    'Request Management': 'Request'
  };
  return acronymMap[module] || module.split(' ')[0]; // Default to first word
};

// Helper to get language flag emoji
const getLanguageFlag = (language) => {
  const flagMap = {
    'English': '🇬🇧',
    'French': '🇫🇷',
    'German': '🇩🇪',
    'Spanish': '🇪🇸',
    'Italian': '🇮🇹',
    'Portuguese': '🇵🇹',
    'Dutch': '🇳🇱',
    'Polish': '🇵🇱',
    'Russian': '🇷🇺',
    'Chinese': '🇨🇳',
    'Japanese': '🇯🇵',
    'Korean': '🇰🇷',
    'Arabic': '🇸🇦',
    'Hindi': '🇮🇳',
    'Swedish': '🇸🇪',
    'Norwegian': '🇳🇴',
    'Danish': '🇩🇰',
    'Finnish': '🇫🇮',
    'Czech': '🇨🇿',
    'Greek': '🇬🇷',
    'Turkish': '🇹🇷',
    'Hebrew': '🇮🇱',
    'Thai': '🇹🇭',
    'Vietnamese': '🇻🇳',
    'Indonesian': '🇮🇩',
    'Malay': '🇲🇾'
  };
  
  // Handle both string and object formats
  const langName = typeof language === 'string' ? language : language?.language;
  return flagMap[langName] || '🌐';
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResourcesPageNew = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [resources, setResources] = useState([]);
  const [myResources, setMyResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentResource, setCurrentResource] = useState(null);
  const [usage, setUsage] = useState(null);
  const [idleAlerts, setIdleAlerts] = useState([]);
  
  // Search and view mode states
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'list'
  
  // Represented freelancers (for Partner Manager only)
  const [representedFreelancers, setRepresentedFreelancers] = useState([]);
  const [representedSearchQuery, setRepresentedSearchQuery] = useState('');
  const [representedCollapsed, setRepresentedCollapsed] = useState(false);
  // My Resources collapsible ONLY for Operating Partner, starts collapsed
  const [myResourcesCollapsed, setMyResourcesCollapsed] = useState(false);  // Will be set to true only for Operating Partner in useEffect
  
  // Bonus trial modal state
  const [showBonusTrialModal, setShowBonusTrialModal] = useState(false);
  const [bonusTrialOffer, setBonusTrialOffer] = useState(null);
  const [pendingActivationResourceId, setPendingActivationResourceId] = useState(null);
  const [bonusTrialLoading, setBonusTrialLoading] = useState(false);
  
  // AI Agents section state (collapsed by default)
  const [aiAgentsCollapsed, setAiAgentsCollapsed] = useState(true);
  
  // Check if user is a freelancer
  const isFreelance = user?.is_freelance === true;
  
  // Check if user is Operating Partner (by role, not company_id)
  const isOperatingPartner = isOperatingPartnerRole(user);
  
  // Check if user has any RM-type role (resource_manager or operating_partner)
  const isRM = isRMRole(user);
  
  // Get freelance restrictions
  const { isRepresented } = useFreelanceRestrictions();

  const [formData, setFormData] = useState({
    // Personal Info
    first_name: '',
    last_name: '',
    role: '',
    seniority: 'Medior',
    bio: '',
    
    // Location & Work
    location_city: '',
    location_country: '',
    location_address: '',
    timezone: '',
    remote_policy: '',
    
    // Skills & Certifications
    skills: '',
    certifications: [],
    other_certifications: '',
    linkedin_url: '',
    
    // Pricing (Option B)
    currency: 'EUR',
    pricing_min_daily: '',
    pricing_max_daily: '',
    discount_short_term: '0',
    discount_long_term: '10',
    min_engagement_duration: '',
    
    // Availability (Phase 3C)
    availability_status: 'Available Now',
    available_from: '',
    commitment_hours_week: '',
    commitment_duration_weeks: '',
    availability_notes: '',
    cv_url: '',
    is_activated: true
  });

  // Note: isRM is defined above using isRMRole(user)
  
  // Filter resources based on search query
  const filteredMyResources = useMemo(() => {
    if (!searchQuery.trim()) return myResources;
    const query = searchQuery.toLowerCase();
    return myResources.filter(r => 
      (r.first_name && r.first_name.toLowerCase().includes(query)) ||
      (r.last_name && r.last_name.toLowerCase().includes(query)) ||
      (r.role && r.role.toLowerCase().includes(query)) ||
      (r.location_city && r.location_city.toLowerCase().includes(query)) ||
      (r.location_country && r.location_country.toLowerCase().includes(query)) ||
      (r.skills && typeof r.skills === 'string' && r.skills.toLowerCase().includes(query)) ||
      (r.certifications && Array.isArray(r.certifications) && r.certifications.some(c => typeof c === 'string' && c.toLowerCase().includes(query)))
    );
  }, [myResources, searchQuery]);

  // Filter represented freelancers by search query
  const filteredRepresentedFreelancers = useMemo(() => {
    if (!representedSearchQuery) return representedFreelancers;
    const query = representedSearchQuery.toLowerCase();
    return representedFreelancers.filter(f => {
      const { profile, company, resources } = f;
      const resourceMatch = resources?.some(r => 
        (r.name && r.name.toLowerCase().includes(query)) ||
        (r.role && r.role.toLowerCase().includes(query)) ||
        (r.city && r.city.toLowerCase().includes(query)) ||
        (r.country && r.country.toLowerCase().includes(query)) ||
        (r.skills && r.skills.some(s => s.toLowerCase().includes(query))) ||
        (r.certifications && r.certifications.some(c => c.toLowerCase().includes(query)))
      );
      return (
        (profile?.first_name && profile.first_name.toLowerCase().includes(query)) ||
        (profile?.last_name && profile.last_name.toLowerCase().includes(query)) ||
        (company?.name && company.name.toLowerCase().includes(query)) ||
        (company?.city && company.city.toLowerCase().includes(query)) ||
        (f.user?.email && f.user.email.toLowerCase().includes(query)) ||
        resourceMatch
      );
    });
  }, [representedFreelancers, representedSearchQuery]);

  useEffect(() => {
    fetchResources();
    if (isRM) {
      fetchMyResources();
      fetchUsage();
      fetchIdleAlerts();
    }
    // Load represented freelancers for Operating Partner
    // Also set myResourcesCollapsed to true ONLY for Operating Partner
    if (isOperatingPartner) {
      setMyResourcesCollapsed(true);  // Collapse "My Resources" by default only for Operating Partner
      fetchRepresentedFreelancers();
    }
    
    const params = new URLSearchParams(location.search);
    if (params.get('action') === 'create' && isRM) {
      setShowDialog(true);
    }
  }, [location.search, isOperatingPartner]);

  // Handle edit resource from navigation state (from ResourceProfilePage)
  useEffect(() => {
    if (location.state?.editResourceId && myResources.length > 0) {
      const resourceToEdit = myResources.find(r => r.id === location.state.editResourceId);
      if (resourceToEdit) {
        handleEdit(resourceToEdit);
        // Clear the state to prevent re-opening on refresh
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, myResources]);

  const fetchResources = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/resources`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResources(response.data);
    } catch (error) {
      toast.error('Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyResources = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/resources/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyResources(response.data);
    } catch (error) {
      toast.error('Failed to load your resources');
    }
  };
  
  const fetchRepresentedFreelancers = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/partner-manager/represented-freelancers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRepresentedFreelancers(response.data.freelancers || []);
    } catch (error) {
      console.error('Failed to load represented freelancers:', error);
    }
  };

  const fetchUsage = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/resources/activation/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsage(response.data);
    } catch (error) {
      console.error('Failed to load usage data');
    }
  };

  const fetchIdleAlerts = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/resources/idle-alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIdleAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Failed to load idle alerts');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const token = localStorage.getItem('token');

    console.log('📤 Submitting formData:', formData); // Debug
    
    const payload = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      role: formData.role || null,
      seniority: formData.seniority || null,
      work_city: formData.work_city || formData.location_city,
      work_country: formData.work_country || formData.location_country || null,
      city: formData.work_city || formData.location_city,  // Also set old format
      country: formData.work_country || formData.location_country,  // Also set old format
      bio: formData.bio || null,
      cv_url: formData.cv_url || null,
      roles: formData.roles || [],
      experience_years: parseInt(formData.servicenow_experience_years) || parseInt(formData.experience_years) || 0,
      it_experience_years: parseInt(formData.it_experience_years) || 0,
      servicenow_experience_years: parseInt(formData.servicenow_experience_years) || 0,
      modules: formData.modules || [],
      modules_mastered: formData.modules || [],  // Also set new format
      certifications: formData.certifications || [],
      other_certifications: formData.other_certifications || [],
      can_work_remote: formData.can_work_remote || false,
      can_travel: formData.can_travel || false,
      max_travel_distance: formData.max_travel_distance || null,
      max_onsite_days: formData.max_onsite_days || null,
      timezone_min: formData.timezone_min || null,
      timezone_max: formData.timezone_max || null,
      availability_status: formData.availability_status || 'available',
      available_from: formData.available_from || null,
      available_to: formData.available_to || null,  // ADDED: End date for availability
      availability_date: formData.available_from || null,  // Also set new format
      commitment_capacity: formData.commitment_capacity || null,
      availability_capacity: formData.commitment_capacity || null,  // Also set new format
      currency: formData.currency || 'EUR',
      daily_rate: parseFloat(formData.daily_rate),
      long_term_discount: parseFloat(formData.long_term_discount) || 0,
      linkedin: formData.linkedin || null,
      languages_required: formData.languages_required || [],
      languages_spoken: formData.languages_required || [],  // Also set new format
      is_activated: formData.is_activated !== undefined ? formData.is_activated : true,
      // ADDED: AI-Augmented fields
      is_ai_augmented: formData.is_ai_augmented || false,
      ai_agents: formData.ai_agents || [],
      // ADDED: ServiceNow Products (Primary matching signal)
      servicenow_products: formData.servicenow_products || [],
      // ADDED: Security & Compliance fields
      security_clearance: formData.security_clearance || null,
      clearance_country: formData.clearance_country || null,
      nationality: formData.nationality || null,
      residence_country: formData.residence_country || null
    };
    
    console.log('📤 Sending payload:', payload); // Debug

    try {
      let resourceId;
      
      if (editMode && currentResource) {
        await axios.put(`${API}/resources/${currentResource.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        resourceId = currentResource.id;
        toast.success('Resource updated successfully');
      } else {
        const response = await axios.post(`${API}/resources`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        resourceId = response.data.id;
        toast.success('Resource created successfully');
      }
      
      // Upload CV if a file was selected
      if (formData.cv_file && resourceId) {
        try {
          const cvFormData = new FormData();
          cvFormData.append('file', formData.cv_file);
          
          await axios.post(`${API}/resources/${resourceId}/upload-cv`, cvFormData, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          toast.success('CV uploaded successfully');
        } catch (cvError) {
          console.error('CV upload failed:', cvError);
          toast.error('Resource saved but CV upload failed. Please try uploading CV separately.');
        }
      }
      
      setShowDialog(false);
      resetForm();
      fetchMyResources();
      fetchResources();
      fetchUsage();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        // Pydantic validation errors
        const errorMessages = errorDetail.map(err => `${err.loc.join('.')}: ${err.msg}`).join(', ');
        toast.error(errorMessages);
      } else {
        toast.error(errorDetail || 'Operation failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleActivation = async (resourceId, currentStatus) => {
    const token = localStorage.getItem('token');
    const action = currentStatus ? 'deactivate' : 'activate';
    
    try {
      await axios.post(`${API}/resources/${resourceId}/${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Resource ${action}d successfully`);
      fetchMyResources();
      fetchUsage();
    } catch (error) {
      const errorData = error.response?.data?.detail;
      
      // Check if this is a bonus trial offer
      if (error.response?.status === 402 && errorData?.error === 'bonus_trial_available') {
        setBonusTrialOffer(errorData.bonus_trial_offer);
        setPendingActivationResourceId(resourceId);
        setShowBonusTrialModal(true);
        return;
      }
      
      // Handle other errors
      if (typeof errorData === 'object' && errorData?.message) {
        toast.error(errorData.message);
      } else {
        toast.error(errorData || `Failed to ${action} resource`);
      }
    }
  };

  const handleAcceptBonusTrial = async () => {
    const token = localStorage.getItem('token');
    setBonusTrialLoading(true);
    
    try {
      // Accept the bonus trial
      const response = await axios.post(`${API}/subscription/accept-bonus-trial`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message || '🎉 Advanced trial activated!');
      setShowBonusTrialModal(false);
      setBonusTrialOffer(null);
      
      // Now activate the resource
      if (pendingActivationResourceId) {
        await axios.post(`${API}/resources/${pendingActivationResourceId}/activate`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Resource activated successfully!');
        setPendingActivationResourceId(null);
      }
      
      // Refresh data
      fetchMyResources();
      fetchUsage();
      
      // Refresh user data to update subscription info
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate bonus trial');
    } finally {
      setBonusTrialLoading(false);
    }
  };

  const handleEdit = (resource) => {
    console.log('Editing resource:', resource); // Debug
    setEditMode(true);
    setCurrentResource(resource);
    
    // Map all fields from resource to formData with proper defaults
    const mappedData = {
      // Personal Info
      first_name: resource.first_name || resource.name?.split(' ')[0] || '',
      last_name: resource.last_name || resource.name?.split(' ').slice(1).join(' ') || '',
      role: resource.role || '',
      seniority: resource.seniority || 'Medior',
      bio: resource.bio || '',
      
      // Location & Work
      location_city: resource.work_city || resource.city || '',
      location_country: resource.work_country || resource.country || '',
      work_city: resource.work_city || resource.city || '',
      work_country: resource.work_country || resource.country || '',
      
      // Skills & Certifications
      skills: resource.skills || '',
      certifications: resource.certifications || [],
      other_certifications: resource.other_certifications || [],
      linkedin: resource.linkedin || '',  // FIXED: Changed from linkedin_url to linkedin
      
      // Roles & Experience - FIXED: Added all experience fields
      roles: resource.roles || [],
      experience_years: resource.experience_years?.toString() || resource.servicenow_experience_years?.toString() || '',
      it_experience_years: resource.it_experience_years?.toString() || '',
      servicenow_experience_years: resource.servicenow_experience_years?.toString() || resource.experience_years?.toString() || '',
      modules: resource.modules || resource.modules_mastered || [],
      
      // ServiceNow Products - ADDED
      servicenow_products: resource.servicenow_products || [],
      
      // Pricing
      currency: resource.currency || 'EUR',
      daily_rate: resource.daily_rate?.toString() || '',
      long_term_discount: resource.long_term_discount?.toString() || '10',
      
      // Availability - FIXED: Added available_to
      availability_status: resource.availability_status || 'Available Now',
      available_from: resource.available_from ? resource.available_from.split('T')[0] : (resource.availability_date ? resource.availability_date.split('T')[0] : ''),
      available_to: resource.available_to ? resource.available_to.split('T')[0] : '',
      commitment_capacity: resource.commitment_capacity || resource.availability_capacity || '',
      
      // Mobility
      can_work_remote: resource.can_work_remote || false,
      can_travel: resource.can_travel || false,
      max_travel_distance: resource.max_travel_distance || '',
      max_onsite_days: resource.max_onsite_days || null,
      flexible_timezone: resource.flexible_timezone || false,
      
      // Timezone - FIXED: Now correctly mapping these fields
      timezone_min: resource.timezone_min || '',
      timezone_max: resource.timezone_max || '',
      
      // Languages - FIXED: Correctly set languages_required
      languages_required: resource.languages_required || resource.languages_spoken || [],
      
      // AI-Augmented fields - ADDED
      is_ai_augmented: resource.is_ai_augmented || false,
      ai_agents: resource.ai_agents || [],
      
      // Security & Compliance - ADDED
      security_clearance: resource.security_clearance || '',
      clearance_country: resource.clearance_country || '',
      nationality: resource.nationality || '',
      residence_country: resource.residence_country || '',
      
      // System
      cv_url: resource.cv_url || '',
      is_activated: resource.is_activated !== undefined ? resource.is_activated : true
    };
    
    console.log('Mapped formData:', mappedData); // Debug
    setFormData(mappedData);
    setShowDialog(true);
  };

  const handleDelete = async (resourceId) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return;

    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/resources/${resourceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Resource deleted successfully');
      fetchMyResources();
      fetchResources();
      fetchUsage();
    } catch (error) {
      toast.error('Failed to delete resource');
    }
  };

  const handleDuplicate = async (resource) => {
    const token = localStorage.getItem('token');
    try {
      // Create a copy of the resource data
      const duplicateData = {
        ...resource,
        first_name: resource.first_name,
        last_name: resource.last_name + ' (Copy)',
        resource_id: undefined,  // Will be generated by backend
        id: undefined,  // Will be generated by backend
        is_activated: false,  // Create as inactive
        created_at: undefined,
        cv_url: undefined  // Don't copy CV
      };

      // Remove fields that shouldn't be copied
      delete duplicateData._id;
      delete duplicateData.id;
      delete duplicateData.resource_id;

      await axios.post(`${API}/resources`, duplicateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Resource duplicated successfully (inactive)');
      fetchMyResources();
      fetchResources();
      fetchUsage();
    } catch (error) {
      console.error('Duplicate error:', error);
      toast.error('Failed to duplicate resource');
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      work_city: '',
      work_country: '',
      bio: '',
      cv_url: '',
      roles: [],
      experience_years: '',
      modules: [],
      certifications: [],
      can_work_remote: true,
      can_travel: false,
      max_travel_distance: '',
      max_onsite_days: null,
      timezone_min: '',
      timezone_max: '',
      availability_status: 'available',
      available_from: '',
      commitment_capacity: null,
      currency: 'EUR',
      daily_rate: '',
      long_term_discount: '',
      linkedin: '',
      languages_required: [],
      is_activated: true
    });
    setEditMode(false);
    setCurrentResource(null);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'available': { label: 'Available Now', class: 'badge badge-available' },
      'available_now': { label: 'Available Now', class: 'badge badge-available' },
      'Available Now': { label: 'Available Now', class: 'badge badge-available' },
      'available_soon': { label: 'Available Soon', class: 'badge badge-reserved' },
      'Available Soon': { label: 'Available Soon', class: 'badge badge-reserved' },
      'partially_available': { label: 'Partially Available', class: 'badge badge-reserved' },
      'Partially Available': { label: 'Partially Available', class: 'badge badge-reserved' },
      'not_available': { label: 'Not Available', class: 'badge badge-occupied' },
      'Not Available': { label: 'Not Available', class: 'badge badge-occupied' },
      'reserved': { label: 'Reserved', class: 'badge badge-reserved' },
      'occupied': { label: 'On Project', class: 'badge badge-occupied' },
      'on_project': { label: 'On Project', class: 'badge badge-occupied' }
    };
    const config = statusMap[status] || { label: status || 'Unknown', class: 'badge badge-available' };
    return <span className={config.class}>{config.label}</span>;
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="resources-page">

      <main className="max-w-7xl mx-auto px-6 py-8 ">
        <div className="flex items-center justify-between mb-8 fade-in">
          <div>
            <h1 className="text-4xl font-bold mb-2" style={{ color: '#1B3240' }}>
              {isFreelance ? 'My Consultant Profile' : 'Resources'}
            </h1>
            <p className="text-lg text-gray-600">
              {isFreelance 
                ? 'Showcase your ServiceNow expertise to potential clients'
                : 'Manage ServiceNow consultants'
              }
            </p>
          </div>
          {/* Add Resource Button - Hidden for represented freelancers if they already have a profile */}
          {isRM && !(isRepresented && myResources.length > 0) && (
            <Button 
              className="btn-snow"
              onClick={() => {
                resetForm();
                setShowDialog(true);
              }}
              data-testid="add-resource-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {isFreelance ? 'Create Profile' : 'Add Resource'}
            </Button>
          )}
        </div>

        {/* Idle Alerts (RM only) */}
        {isRM && idleAlerts.length > 0 && (
          <Card className="mb-8 bg-amber-50 border border-amber-200">
            <CardHeader>
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-amber-600 mr-2" />
                <CardTitle className="text-amber-900">Idle Resource Alerts</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-amber-800 mb-4">
                {idleAlerts.length} resource(s) may become idle soon
              </p>
              <div className="space-y-2">
                {idleAlerts.slice(0, 3).map((alert, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900">{alert.resource.name}</p>
                      <p className="text-sm text-gray-600">
                        ~{alert.days_until_idle} days until idle
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-amber-700">
                        Potential loss: {alert.currency} {alert.potential_loss.toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* My Resources (RM only) */}
        {isRM && (
          <div className="mb-8">
            {/* Section Header with Chevron for all RM users */}
            <div 
              className="flex items-center justify-between mb-4 cursor-pointer group"
              onClick={() => setMyResourcesCollapsed(!myResourcesCollapsed)}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white rounded-lg shadow-sm border border-gray-200 group-hover:shadow-md transition-shadow">
                  <Users className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold" style={{ color: '#1B3240' }}>
                    {isFreelance ? 'My Consultant Profile' : 'My Human Resources'}
                  </h2>
                  <span className="text-sm text-gray-500">
                    {filteredMyResources.length} {isFreelance ? 'profile(s)' : 'resource(s)'}
                  </span>
                </div>
              </div>
              <button className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                {myResourcesCollapsed ? (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                )}
              </button>
            </div>
            
            {/* Show content when not collapsed */}
            {!myResourcesCollapsed && (
            <>
            {/* Search and View Toggle */}
            {myResources.length > 0 && (
              <SearchAndViewToggle
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                placeholder="Search by name, role, location, skills..."
              />
            )}
            
            {/* List View */}
            {viewMode === 'list' && myResources.length > 0 ? (
              <ResourceListView
                resources={filteredMyResources}
                isOwner={true}
                onToggleActivation={toggleActivation}
                onEdit={handleEdit}
                onNavigate={(id) => navigate(`/resources/${id}`)}
              />
            ) : (
            /* Card View */
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredMyResources.length === 0 && searchQuery ? (
                <div className="col-span-full text-center py-8 text-gray-500">
                  No resources match your search "{searchQuery}"
                </div>
              ) : myResources.length === 0 ? (
                <Card 
                  className="card-hover cursor-pointer border-2 border-dashed border-slate-300 hover:border-slate-400 transition-colors bg-white"
                  onClick={() => setShowDialog(true)}
                >
                  <CardContent className="py-16 text-center">
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                        <Plus className="w-8 h-8 text-slate-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">
                        {isFreelance ? 'Create your consultant profile' : 'Add your first resource'}
                      </h3>
                      <p className="text-sm text-slate-600">
                        {isFreelance 
                          ? 'Showcase your ServiceNow expertise and availability to find matching projects'
                          : 'Add your first consultant from your team to make them visible to potential clients'
                        }
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                filteredMyResources.map((resource) => (
                  <Card 
                    key={resource.id} 
                    className="card-hover hover:shadow-lg transition-all duration-200 bg-white border border-gray-200 relative cursor-pointer"
                    onClick={() => navigate(`/resources/${resource.id}`)}
                  >
                  {/* Toggle Active/Inactive - Top Right like project cards */}
                  <div className="absolute top-4 right-4 z-10" onClick={(e) => e.stopPropagation()}>
                    <label className="flex items-center cursor-pointer">
                      <div className="relative">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={resource.is_activated}
                          onChange={() => toggleActivation(resource.id, resource.is_activated)}
                        />
                        <div className={`block w-10 h-6 rounded-full transition-colors ${
                          resource.is_activated ? 'bg-green-500' : 'bg-gray-300'
                        }`}></div>
                        <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${
                          resource.is_activated ? 'transform translate-x-4' : ''
                        }`}></div>
                      </div>
                    </label>
                  </div>

                  <CardHeader className="pb-3 pr-16">
                    <div className="flex items-start gap-2 mb-2">
                      <span className="px-3 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-md border border-blue-200">
                        {resource.resource_id}
                      </span>
                      {resource.is_activated && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-md border border-green-200">
                          <Star className="w-3 h-3 fill-current" />
                          Active
                        </span>
                      )}
                      {/* Quick Match Icon - Hidden for represented freelancers */}
                      {!isRepresented && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/matching', { 
                              state: { 
                                selectedResourceId: resource.id,
                                resourceName: `${resource.first_name || ''} ${resource.last_name || resource.name || ''}`.trim()
                              }
                            });
                          }}
                          className="h-7 px-2 ml-2 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white"
                          title="Find matching projects"
                        >
                          <TrendingUp className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                    <CardTitle className="text-xl font-bold mb-1" style={{ color: '#1B3240' }}>
                      {resource.first_name && resource.last_name 
                        ? `${resource.first_name} ${resource.last_name}` 
                        : resource.name || 'Unknown'}
                    </CardTitle>
                    <p className="text-sm text-gray-600 font-medium">
                      {resource.seniority} {resource.role}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-xs text-gray-500">
                        {resource.experience_years || 0} years experience
                      </p>
                      {resource.is_ai_augmented && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-sm">
                          <Zap className="w-3 h-3" />
                          AI-Augmented
                        </span>
                      )}
                    </div>
                  </CardHeader>

                  <CardContent className="pt-0 space-y-3">
                    {/* ServiceNow Products - Primary matching signal */}
                    {resource.servicenow_products && resource.servicenow_products.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-700 mb-2">Products Mastered</p>
                        <div className="flex flex-wrap gap-2">
                          {resource.servicenow_products.slice(0, 4).map((product, idx) => (
                            <span key={idx} className="px-2 py-1 text-xs font-medium rounded-md bg-purple-50 text-purple-700 border border-purple-200">
                              {typeof product === 'string' ? product.toUpperCase() : product}
                            </span>
                          ))}
                          {resource.servicenow_products.length > 4 && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-md border border-gray-300">
                              +{resource.servicenow_products.length - 4} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Languages Spoken */}
                    {resource.languages_spoken && resource.languages_spoken.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-700 mb-2">Languages</p>
                        <div className="flex flex-wrap gap-2">
                          {resource.languages_spoken.map((lang, idx) => (
                            <span key={idx} className="px-2 py-1 text-xs font-medium rounded-md bg-gray-100 text-gray-700 border border-gray-200 flex items-center gap-1">
                              <span>{getLanguageFlag(lang)}</span>
                              {typeof lang === 'string' ? lang : `${lang.language} • ${lang.level}`}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Modules Mastered */}
                    {((resource.modules_mastered && resource.modules_mastered.length > 0) || (resource.modules && resource.modules.length > 0)) && (
                      <div>
                        <p className="text-xs font-semibold text-gray-700 mb-2">Modules Mastered</p>
                        <div className="flex flex-wrap gap-2">
                          {(resource.modules_mastered || resource.modules || []).slice(0, 3).map((module, idx) => (
                            <span key={idx} className="px-2 py-1 text-xs font-medium rounded-md border bg-blue-50 text-blue-700 border-blue-200">
                              {getModuleAcronym(module)}
                            </span>
                          ))}
                          {(resource.modules_mastered || resource.modules || []).length > 3 && (
                            <button
                              onClick={() => navigate(`/resources/${resource.id}`)}
                              className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-md border border-gray-300 hover:bg-gray-200"
                            >
                              +{(resource.modules_mastered || resource.modules).length - 3} more
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Certified */}
                    {resource.certifications && resource.certifications.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-700 mb-2">Certified</p>
                        <div className="flex flex-wrap gap-2">
                          {resource.certifications.slice(0, 3).map((cert, idx) => (
                            <span key={idx} className="px-2 py-1 text-xs font-medium rounded-md border" style={{ 
                              background: 'rgba(98, 216, 78, 0.1)', 
                              color: '#62D84E',
                              borderColor: 'rgba(98, 216, 78, 0.3)'
                            }}>
                              {getCertificationAcronym(cert)}
                            </span>
                          ))}
                          {resource.certifications.length > 3 && (
                            <button
                              onClick={() => navigate(`/resources/${resource.id}`)}
                              className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-md border border-gray-300 hover:bg-gray-200"
                            >
                              See more
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Company */}
                    {resource.company_name && (
                      <div className="py-2 border-t border-gray-100">
                        <p className="text-xs text-gray-500 mb-1">Company</p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/company/${resource.company_id}`);
                          }}
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {resource.company_name}
                        </button>
                      </div>
                    )}

                    {/* Location, Timezone & Remote */}
                    <div className="flex items-center justify-between py-2 border-t border-gray-100">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center text-sm text-gray-700">
                          <MapPin className="w-4 h-4 mr-1 text-gray-500" />
                          <span className="font-medium">
                            {resource.work_city || resource.city || 'Not specified'}, {resource.work_country || resource.country || ''}
                          </span>
                        </div>
                        {(resource.timezone_min || resource.timezone_max) && (
                          <div className="flex items-center text-xs text-gray-600">
                            <Clock className="w-3 h-3 mr-1" />
                            {resource.timezone_min || 'UTC'} → {resource.timezone_max || 'UTC'}
                          </div>
                        )}
                      </div>
                      {resource.can_work_remote && (
                        <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-md border border-blue-200">
                          Remote OK
                        </span>
                      )}
                    </div>

                    {/* Availability & Rate */}
                    <div className="flex items-center justify-between py-2 border-t border-gray-100">
                      <div className="flex flex-col gap-1">
                        <div>{getStatusBadge(resource.availability_status)}</div>
                        {(resource.available_from || resource.availability_date) && (
                          <div className="flex items-center text-xs text-gray-600">
                            <Calendar className="w-3 h-3 mr-1" />
                            <span>
                              {new Date(resource.available_from || resource.availability_date).toLocaleDateString()}
                              {resource.available_to && ` → ${new Date(resource.available_to).toLocaleDateString()}`}
                            </span>
                          </div>
                        )}
                        {(resource.commitment_capacity || resource.availability_capacity) && (
                          <div className="text-xs text-gray-600">
                            Capacity: {resource.commitment_capacity || resource.availability_capacity}%
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="flex items-center text-base font-bold" style={{ color: '#1B3240' }}>
                          <DollarSign className="w-4 h-4 mr-1" />
                          {resource.currency} {resource.daily_rate}/day
                        </div>
                        {resource.long_term_discount && resource.long_term_discount > 0 && (
                          <div className="text-xs text-green-600">
                            -{resource.long_term_discount}% long term
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions with CV prominent - CV only editable via resource form */}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      {resource.cv_url ? (
                        <Button
                          size="sm"
                          variant="default"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(resource.cv_url, '_blank');
                          }}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid={`view-cv-${resource.id}-btn`}
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          View CV
                        </Button>
                      ) : (
                        <span 
                          className="text-xs text-gray-400 flex items-center cursor-pointer hover:text-blue-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(resource);
                          }}
                          title="Click Edit to add a CV"
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          No CV - Edit to add
                        </span>
                      )}
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(resource);
                          }}
                          className="h-8 px-3"
                          data-testid={`edit-resource-${resource.id}-btn`}
                        >
                          <Edit className="w-4 h-4 text-gray-600" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicate(resource);
                          }}
                          className="h-8 px-3"
                          title="Duplicate resource"
                          data-testid={`duplicate-resource-${resource.id}-btn`}
                        >
                          <Copy className="w-4 h-4 text-blue-600" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(resource.id);
                          }}
                          className="h-8 px-3"
                          data-testid={`delete-resource-${resource.id}-btn`}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                        {/* Matching button - Hidden for represented freelancers */}
                        {!isRepresented && (
                          <Button
                            size="sm"
                            variant="default"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Navigate to matching page with this resource pre-selected
                              navigate('/matching', { 
                                state: { 
                                  selectedResourceId: resource.id,
                                  resourceName: `${resource.first_name || ''} ${resource.last_name || resource.name || ''}`.trim()
                                }
                              });
                            }}
                            className="h-8 px-3 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white"
                            title="Find matching projects for this resource"
                            data-testid={`match-resource-${resource.id}-btn`}
                          >
                            <TrendingUp className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
              )}
            </div>
            )}
            </>
            )}
          </div>
        )}

        {/* My AI Agents Section - Only for regular RM (not freelance, not operating partner) */}
        {isRM && !isFreelance && !isOperatingPartner && (
          <div className="mb-8">
            <div 
              className="flex items-center justify-between mb-4 cursor-pointer group"
              onClick={() => setAiAgentsCollapsed(!aiAgentsCollapsed)}
            >
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold" style={{ color: '#1B3240' }}>
                  <Bot className="w-6 h-6 inline mr-2 text-violet-600" />
                  My AI Agents
                </h2>
                <span className="px-3 py-1 bg-violet-100 text-violet-700 text-sm font-medium rounded-full">
                  Coming Soon
                </span>
              </div>
              <button className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                {aiAgentsCollapsed ? (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                )}
              </button>
            </div>
            
            {!aiAgentsCollapsed && (
              <Card className="border-2 border-dashed border-violet-200 bg-gradient-to-br from-violet-50 to-purple-50">
                <CardContent className="py-12 text-center">
                  <div className="flex flex-col items-center max-w-lg mx-auto">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mb-6 shadow-lg">
                      <Bot className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      Submit your AI Agent to the Marketplace
                    </h3>
                    <p className="text-gray-600 mb-6 leading-relaxed">
                      If you've built a powerful AI agent for ServiceNow delivery tasks, you can publish it to the Snow Exchange Marketplace and get it in front of thousands of potential clients looking for innovative solutions.
                    </p>
                    <div className="bg-white/80 rounded-lg p-4 mb-6 text-left w-full">
                      <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                        <Zap className="w-4 h-4 mr-2 text-amber-500" />
                        How it works
                      </h4>
                      <ul className="text-sm text-gray-600 space-y-2">
                        <li className="flex items-start">
                          <span className="mr-2 text-violet-600 font-bold">1.</span>
                          Schedule a discovery call with our team
                        </li>
                        <li className="flex items-start">
                          <span className="mr-2 text-violet-600 font-bold">2.</span>
                          We'll understand your AI agent's capabilities together
                        </li>
                        <li className="flex items-start">
                          <span className="mr-2 text-violet-600 font-bold">3.</span>
                          We'll configure the optimal listing for your agent
                        </li>
                        <li className="flex items-start">
                          <span className="mr-2 text-violet-600 font-bold">4.</span>
                          Your agent becomes visible to all PMs on the platform
                        </li>
                      </ul>
                    </div>
                    <Button
                      className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white px-6 py-3 h-auto"
                      onClick={() => {
                        // Open email client with pre-filled subject
                        const subject = encodeURIComponent('AI Agent to submit to the Snow Exchange Consultant Marketplace');
                        const body = encodeURIComponent(`Hello Snow Exchange Team,\n\nI would like to submit an AI Agent to the marketplace.\n\nCompany: ${user?.company_name || '[Your Company]'}\nContact: ${user?.email || '[Your Email]'}\n\nBrief description of the AI Agent:\n[Please describe your AI agent and its ServiceNow delivery capabilities]\n\nBest regards`);
                        window.location.href = `mailto:support@snow-exchange.com?subject=${subject}&body=${body}`;
                      }}
                      data-testid="submit-ai-agent-btn"
                    >
                      <Mail className="w-5 h-5 mr-2" />
                      Contact us to submit your AI Agent
                    </Button>
                    <p className="text-xs text-gray-500 mt-4">
                      Not sure if your agent is ready? We're happy to give quick feedback!
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Freelancers I Represent - Operating Partner only */}
        {isOperatingPartner && (
          <div className="mb-8">
            <div 
              className="flex items-center justify-between mb-4 cursor-pointer group"
              onClick={() => setRepresentedCollapsed(!representedCollapsed)}
            >
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold" style={{ color: '#1B3240' }}>
                  <Users className="w-6 h-6 inline mr-2 text-purple-600" />
                  Freelancers I Represent
                </h2>
                <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm font-medium rounded-full">
                  {representedFreelancers.length} freelancer(s)
                </span>
              </div>
              <button className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                {representedCollapsed ? (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                )}
              </button>
            </div>
            
            {!representedCollapsed && (
              <>
                {/* Search bar for represented freelancers */}
                {representedFreelancers.length > 0 && (
                  <div className="mb-4">
                    <input
                      type="text"
                      placeholder="Search by name, skills, location, company..."
                      value={representedSearchQuery}
                      onChange={(e) => setRepresentedSearchQuery(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                )}
                
                {filteredRepresentedFreelancers.length === 0 ? (
                  <Card className="border-2 border-dashed border-purple-200 bg-purple-50/50">
                    <CardContent className="py-12 text-center">
                      <Users className="w-12 h-12 text-purple-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-purple-900 mb-2">
                        No represented freelancers yet
                      </h3>
                      <p className="text-sm text-purple-700">
                        Freelancers who choose the "Represented" plan will appear here.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {/* Active Freelancers Section */}
                    {(() => {
                      const activeFreelancers = filteredRepresentedFreelancers.filter(f => f.has_active_profile);
                      const inactiveFreelancers = filteredRepresentedFreelancers.filter(f => !f.has_active_profile);
                      
                      return (
                        <>
                          {/* Active Freelancers */}
                          {activeFreelancers.length > 0 && (
                            <>
                              <div className="flex items-center gap-2 mb-3">
                                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                                  {activeFreelancers.length} Active
                                </span>
                              </div>
                              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                                {activeFreelancers.map((freelancer) => {
                                  const activeResources = freelancer.resources?.filter(r => r.is_active || r.is_activated) || [];
                                  
                                  return (
                                  <Card 
                                    key={freelancer.user.id}
                                    className="hover:shadow-lg transition-all duration-200 bg-white border border-green-200 relative"
                                  >
                                    {/* Active Badge */}
                                    <div className="absolute top-3 right-3">
                                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                                        Active
                                      </span>
                                    </div>
                                    
                                    <CardContent className="pt-6 pb-4 space-y-4">
                                      {/* Freelancer Info */}
                                      <div>
                                        <h3 className="text-lg font-bold text-gray-900">
                                          {freelancer.profile?.first_name} {freelancer.profile?.last_name}
                                        </h3>
                                        <p className="text-sm text-gray-600">{freelancer.company?.name}</p>
                                        <p className="text-xs text-gray-500 mt-1">{freelancer.user?.email}</p>
                                      </div>
                                      
                                      {/* Location */}
                                      {(freelancer.company?.city || freelancer.company?.country) && (
                                        <div className="flex items-center text-sm text-gray-600">
                                          <MapPin className="w-4 h-4 mr-1 text-gray-400" />
                                          {[freelancer.company?.city, freelancer.company?.country].filter(Boolean).join(', ')}
                                        </div>
                                      )}
                                      
                                      {/* Active Resources/Profiles */}
                                      {activeResources.map((resource) => (
                                        <div 
                                          key={resource.id}
                                          className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                                        >
                                          <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-semibold text-gray-800">
                                              {resource.name || `${resource.first_name || ''} ${resource.last_name || ''}`.trim()}
                                            </span>
                                          </div>
                                          
                                          {/* Only show role/seniority if they exist */}
                                          {(resource.role || resource.seniority) && (
                                            <p className="text-xs text-gray-600 mb-2">
                                              {[resource.role, resource.seniority].filter(Boolean).join(' • ')}
                                            </p>
                                          )}
                                          
                                          {/* Availability Date */}
                                          {resource.availability_date && (
                                            <div className="flex items-center text-xs text-emerald-600 mb-2">
                                              <Calendar className="w-3 h-3 mr-1" />
                                              Available from: {new Date(resource.availability_date).toLocaleDateString()}
                                            </div>
                                          )}
                                          {resource.availability && (
                                            <div className="flex items-center text-xs text-gray-600 mb-2">
                                              <Clock className="w-3 h-3 mr-1" />
                                              {resource.availability}
                                            </div>
                                          )}
                                          
                                          {/* Skills */}
                                          {resource.skills?.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mb-2">
                                              {resource.skills.slice(0, 3).map((skill, idx) => (
                                                <span key={idx} className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs rounded">
                                                  {skill}
                                                </span>
                                              ))}
                                              {resource.skills.length > 3 && (
                                                <span className="text-xs text-gray-500">+{resource.skills.length - 3}</span>
                                              )}
                                            </div>
                                          )}
                                          
                                          {/* Pricing */}
                                          <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                                            <div className="text-xs text-gray-600">
                                              <span>TJM Freelance:</span>
                                              <span className="font-semibold ml-1">{resource.currency} {resource.daily_rate}</span>
                                            </div>
                                            <div className="text-xs">
                                              <span className="text-purple-600">Client:</span>
                                              <span className="font-bold text-purple-700 ml-1">
                                                {resource.currency} {resource.pricing?.client_rate}
                                              </span>
                                              {resource.pricing?.is_custom && (
                                                <span className="text-orange-500 ml-1">(custom)</span>
                                              )}
                                            </div>
                                          </div>
                                          
                                          {/* View Resource Button */}
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => navigate(`/resources/${resource.id}`)}
                                            className="w-full mt-2 text-xs"
                                          >
                                            View Full Profile
                                          </Button>
                                        </div>
                                      ))}
                                      
                                      {/* Actions */}
                                      <div className="flex gap-2 pt-2 border-t border-gray-200">
                                        <Button
                                          size="sm"
                                          variant="default"
                                          onClick={() => {
                                            navigate(`/messages?to=${freelancer.company?.id}&freelancer=${freelancer.user.id}`);
                                          }}
                                          className="flex-1 bg-purple-600 hover:bg-purple-700"
                                        >
                                          <MessageSquare className="w-4 h-4 mr-1" />
                                          Message
                                        </Button>
                                      </div>
                                      
                                      {/* Member Since */}
                                      <p className="text-xs text-gray-400 text-center">
                                        Represented since {new Date(freelancer.user.freelance_choice_made_at).toLocaleDateString()}
                                      </p>
                                    </CardContent>
                                  </Card>
                                  );
                                })}
                              </div>
                            </>
                          )}
                          
                          {/* Inactive/No Profile Freelancers Section */}
                          {inactiveFreelancers.length > 0 && (
                            <>
                              <div className="flex items-center gap-2 mb-3 mt-6">
                                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                                  {inactiveFreelancers.length} Inactive / No Profile
                                </span>
                              </div>
                              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {inactiveFreelancers.map((freelancer) => {
                                  const hasAnyResource = freelancer.resources?.length > 0;
                                  const inactiveResources = freelancer.resources || [];
                                  
                                  return (
                                  <Card 
                                    key={freelancer.user.id}
                                    className="hover:shadow-lg transition-all duration-200 bg-gray-50 border border-gray-300 relative opacity-80"
                                  >
                                    {/* Inactive Badge */}
                                    <div className="absolute top-3 right-3">
                                      <span className="px-2 py-1 bg-gray-200 text-gray-600 text-xs font-medium rounded-full">
                                        {hasAnyResource ? 'Inactive' : 'No Profile'}
                                      </span>
                                    </div>
                                    
                                    <CardContent className="pt-6 pb-4 space-y-4">
                                      {/* Freelancer Info */}
                                      <div>
                                        <h3 className="text-lg font-bold text-gray-700">
                                          {freelancer.profile?.first_name} {freelancer.profile?.last_name}
                                        </h3>
                                        <p className="text-sm text-gray-500">{freelancer.company?.name}</p>
                                        <p className="text-xs text-gray-400 mt-1">{freelancer.user?.email}</p>
                                      </div>
                                      
                                      {/* Location */}
                                      {(freelancer.company?.city || freelancer.company?.country) && (
                                        <div className="flex items-center text-sm text-gray-500">
                                          <MapPin className="w-4 h-4 mr-1 text-gray-400" />
                                          {[freelancer.company?.city, freelancer.company?.country].filter(Boolean).join(', ')}
                                        </div>
                                      )}
                                      
                                      {/* Show inactive resources or no profile message */}
                                      {hasAnyResource ? (
                                        inactiveResources.map((resource) => (
                                          <div 
                                            key={resource.id}
                                            className="p-3 bg-white rounded-lg border border-gray-200"
                                          >
                                            <div className="flex items-center justify-between mb-2">
                                              <span className="text-sm font-semibold text-gray-600">
                                                {resource.name || `${resource.first_name || ''} ${resource.last_name || ''}`.trim()}
                                              </span>
                                              <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500">
                                                Deactivated
                                              </span>
                                            </div>
                                            
                                            {(resource.role || resource.seniority) && (
                                              <p className="text-xs text-gray-500 mb-2">
                                                {[resource.role, resource.seniority].filter(Boolean).join(' • ')}
                                              </p>
                                            )}
                                            
                                            {/* Pricing (grayed out) */}
                                            <div className="flex items-center justify-between pt-2 border-t border-gray-200 text-gray-400">
                                              <div className="text-xs">
                                                <span>TJM:</span>
                                                <span className="font-semibold ml-1">{resource.currency} {resource.daily_rate}</span>
                                              </div>
                                            </div>
                                          </div>
                                        ))
                                      ) : (
                                        <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200 text-center">
                                          <AlertTriangle className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
                                          <p className="text-sm text-yellow-700">
                                            This freelancer hasn't created a consultant profile yet.
                                          </p>
                                        </div>
                                      )}
                                      
                                      {/* Actions - Encourage to activate */}
                                      <div className="flex gap-2 pt-2 border-t border-gray-200">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => {
                                            navigate(`/messages?to=${freelancer.company?.id}&freelancer=${freelancer.user.id}`);
                                          }}
                                          className="flex-1 border-purple-300 text-purple-600 hover:bg-purple-50"
                                        >
                                          <MessageSquare className="w-4 h-4 mr-1" />
                                          {hasAnyResource ? 'Encourage to Activate' : 'Request Profile Creation'}
                                        </Button>
                                      </div>
                                      
                                      {/* Member Since */}
                                      <p className="text-xs text-gray-400 text-center">
                                        Represented since {new Date(freelancer.user.freelance_choice_made_at).toLocaleDateString()}
                                      </p>
                                    </CardContent>
                                  </Card>
                                  );
                                })}
                              </div>
                            </>
                          )}
                        </>
                      );
                    })()}
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* Create/Edit Resource Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ color: '#1B3240' }}>
                {editMode ? 'Edit Resource' : 'Add New Resource'}
              </DialogTitle>
            </DialogHeader>
            <ResourceFormComplete 
              formData={formData}
              setFormData={setFormData}
              onSubmit={handleSubmit}
              onCancel={() => {
                setShowDialog(false);
                resetForm();
              }}
              loading={loading}
            />
          </DialogContent>
        </Dialog>
        
        {/* Bonus Trial Modal */}
        <BonusTrialModal
          isOpen={showBonusTrialModal}
          onClose={() => {
            setShowBonusTrialModal(false);
            setBonusTrialOffer(null);
            setPendingActivationResourceId(null);
          }}
          onAccept={handleAcceptBonusTrial}
          offer={bonusTrialOffer}
          loading={bonusTrialLoading}
        />
      </main>
    </div>
  );
};

export default ResourcesPageNew;
